import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import {
  AdminVacationSchedulesListItem,
  SearchVacationScheduleItem,
  VacationsService,
} from '@app/gen/vacations';
import { Entity } from '@app/types';

import { SettingsState, SettingsWithRootState } from '../../settings.state';
import { VacationsListMapper } from '../../../mappers';
import { VacationCompanySchedulesParams } from '../../../types';

export const getVacationsCompanies = createAsyncThunk<
  AdminVacationSchedulesListItem[],
  void,
  ThunkExtra
>(
  'settings/vacations/getCompanies',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const vacationsService = inject(VacationsService);

      const result = await vacationsService.getVacationScheduleAdminList();

      return result.companies;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getVacationScheduleNames = createAsyncThunk<
  SearchVacationScheduleItem[],
  string,
  ThunkExtra
>(
  'settings/vacations/getScheduleNames',
  async (companyId, { rejectWithValue, extra: { inject } }) => {
    try {
      const vacationsService = inject(VacationsService);

      const result = await vacationsService.searchVacationSchedules({
        query: { company_id: companyId },
      });

      return result.items;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getVacationCompanySchedules = createAsyncThunk<
  AdminVacationSchedulesListItem['vacation_schedules'],
  VacationCompanySchedulesParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/vacations/getCompanySchedules',
  async (
    { companyId, names },
    { rejectWithValue, getState, extra: { inject } },
  ) => {
    try {
      const vacationsService = inject(VacationsService);
      const mapper = inject(VacationsListMapper);

      const scheduleNames =
        getState().settings.vacationsList.scheduleNames[companyId] ?? [];

      const ids = mapper.processScheduleIdsByNames(scheduleNames, names);

      const result = await vacationsService.getVacationScheduleAdminList(
        ids.length ? { body: { ids } } : undefined,
      );

      const company = result.companies.find(
        ({ company_id }) => company_id === companyId,
      );

      return company?.vacation_schedules ?? [];
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deleteVacationSchedule = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    scheduleId: string;
    companyName: string;
    year: number;
  }>,
  ThunkExtra
>(
  'settings/vacations/deleteSchedule',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { companyName, scheduleId, year } = values;

    try {
      const vacationsService = inject(VacationsService);

      await vacationsService.deleteVacationSchedule({
        params: { schedule_id: scheduleId },
      });

      dispatch(
        showCustomNotification([
          {
            title: 'Планирование удалено!',
            type: 'info',
            subtitle: companyName,
            message: `График отпусков ${year}`,
          },
        ]),
      );

      actions.resolve(null);

      dispatch(getVacationsCompanies());
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось удалить планирование!',
            subtitle: companyName,
            message: `График отпусков ${year}`,
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsVacationsListReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getVacationsCompanies.pending, (state) => {
    state.vacationsList.status = 'loading';
    state.vacationsList.schedulesStatus = 'idle';
  });
  builder.addCase(getVacationsCompanies.fulfilled, (state, { payload }) => {
    state.vacationsList.status = 'complete';
    state.vacationsList.companies = payload;
  });
  builder.addCase(
    getVacationsCompanies.rejected,
    (state, { payload, error, meta }) => {
      if (!meta.aborted) {
        state.vacationsList.status = 'failed';
        state.vacationsList.error =
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError);
      }
    },
  );

  builder.addCase(
    getVacationScheduleNames.fulfilled,
    (state, { payload, meta }) => {
      state.vacationsList.scheduleNames[meta.arg] = payload;
    },
  );
  builder.addCase(getVacationScheduleNames.rejected, (state, { meta }) => {
    if (!meta.aborted) {
      state.vacationsList.scheduleNames[meta.arg] = [];
    }
  });

  builder.addCase(getVacationCompanySchedules.pending, (state) => {
    state.vacationsList.schedulesStatus = 'loading';
    state.vacationsList.schedulesError = null;
  });
  builder.addCase(
    getVacationCompanySchedules.fulfilled,
    (state, { payload, meta }) => {
      const company = state.vacationsList.companies.find(
        ({ company_id }) => company_id === meta.arg.companyId,
      );

      if (company) {
        company.vacation_schedules = payload;
      }

      state.vacationsList.schedulesStatus = 'complete';
    },
  );
  builder.addCase(
    getVacationCompanySchedules.rejected,
    (state, { payload, error, meta }) => {
      if (!meta.aborted) {
        state.vacationsList.schedulesStatus = 'failed';
        state.vacationsList.schedulesError =
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError);
      }
    },
  );
};
